import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import FeedSubscribed from "./FeedSubscribed";
import discoverStore from "../store";
import userStore from "../../../Stores/User";
import { CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import HelpIcon from '@mui/icons-material/Help';
import { Tooltip } from '@mui/material';

const SubscribedFeeds = observer(() => {
  const { 
    userFeeds, 
    allFeeds, 
    feedsLoading, 
    allFeedsLoading, 
    fetchUserFeeds, 
    fetchAllFeeds,
    unsubscribeFromFeed, 
    changeCurrentArticle, 
    currentArticle,
    search,
    pendingSearch,
    mode,
    changeMode
  } = discoverStore;
  const [error, setError] = useState(null);
  const [visibleUnsubscribedCount, setVisibleUnsubscribedCount] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const loaderRef = useRef(null);
  
  useEffect(() => {
    let isInitialLoad = true;
    
    if (userStore.idToken && isInitialLoad) {
      try {
        fetchUserFeeds();
        fetchAllFeeds();
        isInitialLoad = false;
      } catch (err) {
        console.error("Error fetching feeds:", err);
        setError("Failed to load your feeds. Please try again later.");
      }
    }
  }, []);

  // Memoize the filter functions
  const subscribedFeedIds = useMemo(() => 
    new Set((userFeeds || []).map(feed => feed.feed_id)), 
    [userFeeds]
  );
  
  const unsubscribedFeedsAll = useMemo(() => 
    (allFeeds || []).filter(feed => !subscribedFeedIds.has(feed.feed_id)),
    [allFeeds, subscribedFeedIds]
  );

  const searchTerm = useMemo(() => 
    pendingSearch.toLowerCase().trim(),
    [pendingSearch]
  );
  
  const filteredUserFeeds = useMemo(() => 
    searchTerm 
      ? (userFeeds || []).filter(feed => 
          feed.title?.toLowerCase().includes(searchTerm) || 
          feed.description?.toLowerCase().includes(searchTerm) ||
          feed.author?.toLowerCase().includes(searchTerm) ||
          feed.url?.toLowerCase().includes(searchTerm)
        )
      : userFeeds,
    [userFeeds, searchTerm]
  );

  const filteredUnsubscribedFeeds = useMemo(() => 
    searchTerm 
      ? unsubscribedFeedsAll.filter(feed => 
          feed.title?.toLowerCase().includes(searchTerm) || 
          feed.description?.toLowerCase().includes(searchTerm) ||
          feed.author?.toLowerCase().includes(searchTerm) ||
          feed.url?.toLowerCase().includes(searchTerm)
        )
      : unsubscribedFeedsAll,
    [unsubscribedFeedsAll, searchTerm]
  );

  // Remove the placeholder filters
  const filteredUserFeedsWithoutPlaceholders = filteredUserFeeds;
  const filteredUnsubscribedFeedsWithoutPlaceholders = filteredUnsubscribedFeeds;

  // Define feed categories based on content analysis
  const feedCategories = useMemo(() => {
    const categories = {
      all: { label: 'All Feeds', count: (allFeeds || []).length },
      tech: { label: 'Technology', count: 0 },
      news: { label: 'News & Politics', count: 0 },
      business: { label: 'Business & Finance', count: 0 },
      science: { label: 'Science & Research', count: 0 },
      culture: { label: 'Culture & Arts', count: 0 }
    };

    // Count feeds in each category
    (allFeeds || []).forEach(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      if (title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
          title.includes('software') || desc.includes('software') ||
          title.includes('programming') || desc.includes('programming') ||
          title.includes('ai') || desc.includes('ai') ||
          title.includes('artificial intelligence') || desc.includes('artificial intelligence')) {
        categories.tech.count++;
      }
      if (title.includes('news') || desc.includes('news') || url.includes('news') ||
          title.includes('politics') || desc.includes('politics') ||
          title.includes('current events') || desc.includes('current events')) {
        categories.news.count++;
      }
      if (title.includes('business') || desc.includes('business') || url.includes('business') ||
          title.includes('finance') || desc.includes('finance') ||
          title.includes('economy') || desc.includes('economy') ||
          title.includes('market') || desc.includes('market')) {
        categories.business.count++;
      }
      if (title.includes('science') || desc.includes('science') || url.includes('science') ||
          title.includes('research') || desc.includes('research') ||
          title.includes('health') || desc.includes('health') ||
          title.includes('medicine') || desc.includes('medicine')) {
        categories.science.count++;
      }
      if (title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
          title.includes('art') || desc.includes('art') ||
          title.includes('music') || desc.includes('music') ||
          title.includes('literature') || desc.includes('literature') ||
          title.includes('film') || desc.includes('film')) {
        categories.culture.count++;
      }
    });

    return categories;
  }, [allFeeds]);

  // Filter feeds based on selected category
  const filterFeedsByCategory = useCallback((feeds) => {
    if (selectedCategory === 'all') return feeds;

    return feeds.filter(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      switch (selectedCategory) {
        case 'tech':
          return title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
                 title.includes('software') || desc.includes('software') ||
                 title.includes('programming') || desc.includes('programming') ||
                 title.includes('ai') || desc.includes('ai') ||
                 title.includes('artificial intelligence') || desc.includes('artificial intelligence');
        case 'news':
          return title.includes('news') || desc.includes('news') || url.includes('news') ||
                 title.includes('politics') || desc.includes('politics') ||
                 title.includes('current events') || desc.includes('current events');
        case 'business':
          return title.includes('business') || desc.includes('business') || url.includes('business') ||
                 title.includes('finance') || desc.includes('finance') ||
                 title.includes('economy') || desc.includes('economy') ||
                 title.includes('market') || desc.includes('market');
        case 'science':
          return title.includes('science') || desc.includes('science') || url.includes('science') ||
                 title.includes('research') || desc.includes('research') ||
                 title.includes('health') || desc.includes('health') ||
                 title.includes('medicine') || desc.includes('medicine');
        case 'culture':
          return title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
                 title.includes('art') || desc.includes('art') ||
                 title.includes('music') || desc.includes('music') ||
                 title.includes('literature') || desc.includes('literature') ||
                 title.includes('film') || desc.includes('film');
        default:
          return true;
      }
    });
  }, [selectedCategory]);

  // Apply category filter to both subscribed and unsubscribed feeds
  const filteredUserFeedsWithCategory = useMemo(() => 
    filterFeedsByCategory(filteredUserFeedsWithoutPlaceholders),
    [filterFeedsByCategory, filteredUserFeedsWithoutPlaceholders]
  );

  const filteredUnsubscribedFeedsWithCategory = useMemo(() => 
    filterFeedsByCategory(filteredUnsubscribedFeedsWithoutPlaceholders),
    [filterFeedsByCategory, filteredUnsubscribedFeedsWithoutPlaceholders]
  );

  const handleUnsubscribe = useCallback((feedId) => {
    try {
      unsubscribeFromFeed(feedId);
    } catch (err) {
      console.error("Error unsubscribing from feed:", err);
    }
  }, [unsubscribeFromFeed]);

  const handleSubscribe = useCallback((feedId) => {
    try {
      const feed = allFeeds.find(f => f.feed_id === feedId);
      if (feed) {
        discoverStore.subscribeToFeed(feed);
      }
    } catch (err) {
      console.error("Error subscribing to feed:", err);
    }
  }, [allFeeds]);

  const loadMoreUnsubscribedFeeds = useCallback(() => {
    if (!isLoadingMore && filteredUnsubscribedFeedsWithoutPlaceholders.length > visibleUnsubscribedCount) {
      setIsLoadingMore(true);
      // Simulate network delay for smoother UX
      setTimeout(() => {
        setVisibleUnsubscribedCount(prev => prev + 10);
        setIsLoadingMore(false);
      }, 300);
    }
  }, [isLoadingMore, visibleUnsubscribedCount, filteredUnsubscribedFeedsWithoutPlaceholders]);

  // Reset visible count when search changes to avoid showing a blank list
  useEffect(() => {
    setVisibleUnsubscribedCount(10);
  }, [searchTerm]);

  // Set up the intersection observer for infinite scroll
  useEffect(() => {
    // Only set up observer if we have more feeds to load
    if (filteredUnsubscribedFeedsWithoutPlaceholders.length <= visibleUnsubscribedCount || !loaderRef.current) {
      return;
    }

    const options = {
      root: null, // viewport
      rootMargin: '200px 0px', // load more before reaching the element
      threshold: 0.1, // trigger when just 10% of the element is visible
    };

    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMoreUnsubscribedFeeds();
      }
    };

    const observer = new IntersectionObserver(handleObserver, options);
    observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loadMoreUnsubscribedFeeds, filteredUnsubscribedFeedsWithoutPlaceholders.length, visibleUnsubscribedCount]);

  if (error) {
    return (
      <div className="article-container">
        <div className="feeds-error-container" style={{ textAlign: "center", padding: "20px", color: "#f44336" }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (feedsLoading || allFeedsLoading) {
    return (
      <div className="article-container">
        <div className="feeds-loading-container" style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: '100px 20px',
          gap: '20px'
        }}>
          <CircularProgress 
            size={40} 
            sx={{
              color: '#4292fa',
              '@keyframes gradient': {
                '0%': { color: '#4292fa' },
                '25%': { color: '#8535cd' },
                '50%': { color: '#e82186' },
                '75%': { color: '#ff5c57' },
                '100%': { color: '#4292fa' }
              },
              animation: 'gradient 2s ease infinite'
            }}
          />
          <p style={{ 
            color: '#fff',
            fontSize: '16px',
            fontWeight: 500,
            background: 'linear-gradient(90deg, #4292fa, #8535cd, #e82186, #ff5c57)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundSize: '300% 100%',
            animation: 'gradient 2s ease infinite'
          }}>Loading feeds...</p>
        </div>
      </div>
    );
  }
  
  // Show a message when no feeds match the search
  if (searchTerm && filteredUserFeedsWithoutPlaceholders.length === 0 && filteredUnsubscribedFeedsWithoutPlaceholders.length === 0) {
    return (
      <div className="article-container" style={{ padding: "40px 20px", textAlign: "center" }}>
        <p style={{ 
          color: '#fff',
          fontSize: '16px',
          opacity: 0.8
        }}>No feeds found matching "{searchTerm}"</p>
      </div>
    );
  }

  return (
    <>


      {/* Category Filter */}
      <div style={{
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        margin: '20px auto 0 auto',
        padding: '0 16px',
        width: '100%',
        maxWidth: '1200px',
        zIndex: 10,
        position: 'relative',
        pointerEvents: 'auto',
      }}>
        {Object.entries(feedCategories).map(([key, { label, count }]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            style={{
              background: selectedCategory === key ? '#4292fa' : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              pointerEvents: 'auto',
              zIndex: 10
            }}
          >
            {label}
            <span style={{
              background: selectedCategory === key ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px'
            }}>
              {count}
            </span>
          </button>
        ))}
      </div>
      {/* End Category Filter */}

      {/* Subscribed Feeds Section */}
      {filteredUserFeedsWithCategory && filteredUserFeedsWithCategory.length > 0 && (
        <div className="article-container" style={{ 
          marginTop: "20px",
          marginBottom: window.innerWidth <= 768 ? "150px" : "0px",
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(247px, 1fr))',
          gap: '10px',
          justifyItems: 'center',
          padding: '0 10px'
        }}>
          <div className="section-header" style={{ 
            marginBottom: "30px",
            gridColumn: '1 / -1',
            width: '100%'
          }}>
            <div className="divider-line"></div>
            <h2>My Feeds {searchTerm && `(${filteredUserFeedsWithCategory.length} results)`}</h2>
            <div className="divider-line"></div>
          </div>
          {filteredUserFeedsWithCategory.map((feed) => (
            <FeedSubscribed
              key={feed.feed_id || feed.id || Math.random().toString()}
              feed={feed}
              idToken={userStore.idToken}
              selectArticle={changeCurrentArticle}
              isActive={currentArticle && currentArticle.article_id === feed.article_id}
              onUnsubscribe={handleUnsubscribe}
              isSubscribed={true}
            />
          ))}
        </div>
      )}

      {/* All Feeds Section with Infinite Scroll */}
      {filteredUnsubscribedFeedsWithCategory && filteredUnsubscribedFeedsWithCategory.length > 0 && (
        <div className="article-container" style={{ 
          marginTop: filteredUserFeedsWithCategory.length === 0 ? "50px" : window.innerWidth <= 768 ? "0px" : "0px",
          marginBottom: "50px",
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(247px, 1fr))',
          gap: '10px',
          justifyItems: 'center',
          padding: '0 10px'
        }}>
          <div className="section-header" style={{ 
            marginTop: "0px", 
            marginBottom: "30px",
            gridColumn: '1 / -1',
            width: '100%'
          }}>
            <div className="divider-line"></div>
            <h2>All Feeds {searchTerm && `(${filteredUnsubscribedFeedsWithCategory.length} results)`}</h2>
            <div className="divider-line"></div>
          </div>
          {filteredUnsubscribedFeedsWithCategory.slice(0, visibleUnsubscribedCount).map((feed) => (
            <FeedSubscribed
              key={feed.feed_id || feed.id || Math.random().toString()}
              feed={feed}
              idToken={userStore.idToken}
              selectArticle={changeCurrentArticle}
              isActive={currentArticle && currentArticle.article_id === feed.article_id}
              onUnsubscribe={handleSubscribe}
              isSubscribed={false}
            />
          ))}
          
          {/* Intersection Observer Target for Infinite Scroll */}
          {filteredUnsubscribedFeedsWithCategory.length > visibleUnsubscribedCount && (
            <div 
              ref={loaderRef} 
              style={{ 
                width: '100%', 
                height: '40px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                padding: '20px 0',
                opacity: isLoadingMore ? 1 : 0
              }}
            >
              <CircularProgress 
                size={24} 
                sx={{
                  color: 'rgba(66, 146, 250, 0.7)',
                }}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Bottom spacer to prevent player overlap */}
      <div style={{ 
        width: '100%', 
        height: '150px', 
        position: 'relative',
        zIndex: 1
      }} />
    </>
  );
});

export default SubscribedFeeds; 